<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';
import lumeratecConstants from 'dashboard/constants/globals';
import { useUISettings } from 'dashboard/composables/useUISettings';
import {
  ACCESSIBILITY_UI_SETTINGS_KEY,
  createDefaultAccessibilityPreferences,
  normalizeAccessibilityPreferences,
} from './accessibilityPreferences';
import {
  getConversationContactName,
  isEditableElement,
  rankAssignmentCandidates,
} from './callCenterAccessibility';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const store = useStore();
const currentUser = useMapGetter('getCurrentUser');
const currentAccountId = useMapGetter('getCurrentAccountId');
const selectedChat = useMapGetter('getSelectedChat');
const { uiSettings } = useUISettings();

const isOpen = ref(false);
const query = ref('');
const activeIndex = ref(0);
const isAssigning = ref(false);
const searchInput = ref(null);
const dialog = ref(null);
const lastFocusedElement = ref(null);
let landmarkIndex = -1;

const copy = computed(() => ({
  title: t('ACCESSIBILITY.ACTION_CENTER.TITLE'),
  description: t('ACCESSIBILITY.ACTION_CENTER.DESCRIPTION'),
  close: t('ACCESSIBILITY.ACTION_CENTER.CLOSE'),
  closeLabel: t('ACCESSIBILITY.ACTION_CENTER.CLOSE_LABEL'),
  searchLabel: t('ACCESSIBILITY.ACTION_CENTER.SEARCH_LABEL'),
  searchPlaceholder: t('ACCESSIBILITY.ACTION_CENTER.SEARCH_PLACEHOLDER'),
  empty: t('ACCESSIBILITY.ACTION_CENTER.EMPTY'),
  actionsLabel: t('ACCESSIBILITY.ACTION_CENTER.ACTIONS_LABEL'),
}));

const preferences = computed(() =>
  normalizeAccessibilityPreferences(
    uiSettings.value?.[ACCESSIBILITY_UI_SETTINGS_KEY] ||
      createDefaultAccessibilityPreferences()
  )
);

const accountId = computed(
  () => route.params.accountId || currentAccountId.value
);

const announce = (message, priority = 'polite') => {
  window.dispatchEvent(
    new CustomEvent('a11y-announce', { detail: { message, priority } })
  );
};

const requestExistingAction = action => {
  window.dispatchEvent(new CustomEvent('a11y-command', { detail: { action } }));
};

const navigateTo = async (name, label) => {
  await router.push({ name, params: { accountId: accountId.value } });
  announce(t('ACCESSIBILITY.ACTION_CENTER.PAGE_OPENED', { label }));
};

const updateAssignee = async (conversation, agent, successMessage) => {
  const response = await ConversationApi.assignAgent({
    conversationId: conversation.id,
    agentId: agent?.id || null,
  });
  await store.dispatch('setCurrentChatAssignee', {
    conversationId: conversation.id,
    assignee: agent ? response.data || agent : null,
  });
  announce(successMessage, 'assertive');
};

const assignCurrentConversation = async () => {
  if (!selectedChat.value?.id) {
    announce(
      t('ACCESSIBILITY.ACTION_CENTER.NO_OPEN_CONVERSATION_TO_CLAIM'),
      'assertive'
    );
    return;
  }
  if (selectedChat.value.meta?.assignee?.id === currentUser.value?.id) {
    announce(t('ACCESSIBILITY.ACTION_CENTER.ALREADY_ASSIGNED_TO_YOU'));
    return;
  }

  try {
    isAssigning.value = true;
    await updateAssignee(
      selectedChat.value,
      currentUser.value,
      t('ACCESSIBILITY.ACTION_CENTER.CONVERSATION_ASSIGNED_TO_YOU', {
        contact: getConversationContactName(selectedChat.value),
      })
    );
  } catch (error) {
    announce(
      error.response?.data?.message ||
        t('ACCESSIBILITY.ACTION_CENTER.CLAIM_FAILED'),
      'assertive'
    );
  } finally {
    isAssigning.value = false;
  }
};

const returnCurrentConversationToQueue = async () => {
  if (!selectedChat.value?.id) {
    announce(
      t('ACCESSIBILITY.ACTION_CENTER.NO_OPEN_CONVERSATION_TO_RETURN'),
      'assertive'
    );
    return;
  }

  try {
    isAssigning.value = true;
    const contactName = getConversationContactName(selectedChat.value);
    await updateAssignee(
      selectedChat.value,
      null,
      t('ACCESSIBILITY.ACTION_CENTER.CONVERSATION_RETURNED_TO_QUEUE', {
        contact: contactName,
      })
    );
  } catch (error) {
    announce(
      error.response?.data?.message ||
        t('ACCESSIBILITY.ACTION_CENTER.RETURN_TO_QUEUE_FAILED'),
      'assertive'
    );
  } finally {
    isAssigning.value = false;
  }
};

const claimNextConversation = async () => {
  if (isAssigning.value) {
    announce(t('ACCESSIBILITY.ACTION_CENTER.ASSIGNMENT_IN_PROGRESS'));
    return;
  }

  try {
    isAssigning.value = true;
    announce(t('ACCESSIBILITY.ACTION_CENTER.SEARCHING_NEXT_CONVERSATION'));
    const response = await ConversationApi.get({
      status: lumeratecConstants.STATUS_TYPE.OPEN,
      assigneeType: lumeratecConstants.ASSIGNEE_TYPE.UNASSIGNED,
      page: 1,
      sortBy: lumeratecConstants.SORT_BY_TYPE.PRIORITY_DESC_CREATED_AT_ASC,
    });
    const payload =
      response.data?.data?.payload || response.data?.payload || [];
    const [conversation] = rankAssignmentCandidates(payload);

    if (!conversation) {
      announce(
        t('ACCESSIBILITY.ACTION_CENTER.NO_CONVERSATION_AVAILABLE'),
        'assertive'
      );
      return;
    }

    await updateAssignee(
      conversation,
      currentUser.value,
      t('ACCESSIBILITY.ACTION_CENTER.CONVERSATION_ASSIGNED')
    );
    await router.push(
      frontendURL(
        conversationUrl({
          accountId: accountId.value,
          id: conversation.id,
        })
      )
    );
    announce(
      // Both keys are fixed translations selected from the SLA state.
      // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      t(
        conversation.sla_missed_events?.length
          ? 'ACCESSIBILITY.ACTION_CENTER.NEXT_CONVERSATION_CLAIMED_SLA'
          : 'ACCESSIBILITY.ACTION_CENTER.NEXT_CONVERSATION_CLAIMED',
        { contact: getConversationContactName(conversation) }
      ),
      'assertive'
    );
  } catch (error) {
    announce(
      error.response?.data?.message ||
        t('ACCESSIBILITY.ACTION_CENTER.CLAIM_NEXT_FAILED'),
      'assertive'
    );
  } finally {
    isAssigning.value = false;
  }
};

const actions = computed(() => [
  {
    id: 'claim-next',
    label: t('ACCESSIBILITY.ACTION_CENTER.ACTIONS.CLAIM_NEXT.LABEL'),
    description: t(
      'ACCESSIBILITY.ACTION_CENTER.ACTIONS.CLAIM_NEXT.DESCRIPTION'
    ),
    shortcut: 'Alt Shift G',
    keywords: t('ACCESSIBILITY.ACTION_CENTER.ACTIONS.CLAIM_NEXT.KEYWORDS'),
    run: claimNextConversation,
  },
  {
    id: 'assign-current',
    label: t('ACCESSIBILITY.ACTION_CENTER.ACTIONS.ASSIGN_CURRENT.LABEL'),
    description: t(
      'ACCESSIBILITY.ACTION_CENTER.ACTIONS.ASSIGN_CURRENT.DESCRIPTION'
    ),
    shortcut: 'Alt Shift U',
    keywords: t('ACCESSIBILITY.ACTION_CENTER.ACTIONS.ASSIGN_CURRENT.KEYWORDS'),
    run: assignCurrentConversation,
  },
  {
    id: 'return-queue',
    label: t('ACCESSIBILITY.ACTION_CENTER.ACTIONS.RETURN_QUEUE.LABEL'),
    description: t(
      'ACCESSIBILITY.ACTION_CENTER.ACTIONS.RETURN_QUEUE.DESCRIPTION'
    ),
    shortcut: 'Alt Shift Q',
    keywords: t('ACCESSIBILITY.ACTION_CENTER.ACTIONS.RETURN_QUEUE.KEYWORDS'),
    run: returnCurrentConversationToQueue,
  },
  {
    id: 'briefing',
    label: t('ACCESSIBILITY.ACTION_CENTER.ACTIONS.BRIEFING.LABEL'),
    description: t('ACCESSIBILITY.ACTION_CENTER.ACTIONS.BRIEFING.DESCRIPTION'),
    shortcut: 'Alt Shift L',
    keywords: t('ACCESSIBILITY.ACTION_CENTER.ACTIONS.BRIEFING.KEYWORDS'),
    run: () => requestExistingAction('briefing'),
  },
  {
    id: 'summary',
    label: t('ACCESSIBILITY.ACTION_CENTER.ACTIONS.SUMMARY.LABEL'),
    description: t('ACCESSIBILITY.ACTION_CENTER.ACTIONS.SUMMARY.DESCRIPTION'),
    shortcut: 'Alt Shift S',
    keywords: t('ACCESSIBILITY.ACTION_CENTER.ACTIONS.SUMMARY.KEYWORDS'),
    run: () => requestExistingAction('summary'),
  },
  {
    id: 'describe-image',
    label: t('ACCESSIBILITY.ACTION_CENTER.ACTIONS.DESCRIBE_IMAGE.LABEL'),
    description: t(
      'ACCESSIBILITY.ACTION_CENTER.ACTIONS.DESCRIBE_IMAGE.DESCRIPTION'
    ),
    shortcut: 'Alt Shift I',
    keywords: t('ACCESSIBILITY.ACTION_CENTER.ACTIONS.DESCRIBE_IMAGE.KEYWORDS'),
    run: () => requestExistingAction('describe-image'),
  },
  {
    id: 'focus-conversations',
    label: t('ACCESSIBILITY.ACTION_CENTER.ACTIONS.FOCUS_CONVERSATIONS.LABEL'),
    description: t(
      'ACCESSIBILITY.ACTION_CENTER.ACTIONS.FOCUS_CONVERSATIONS.DESCRIPTION'
    ),
    shortcut: 'Alt Shift C',
    keywords: t(
      'ACCESSIBILITY.ACTION_CENTER.ACTIONS.FOCUS_CONVERSATIONS.KEYWORDS'
    ),
    run: () => requestExistingAction('focus-conversation-list'),
  },
  {
    id: 'focus-reply',
    label: t('ACCESSIBILITY.ACTION_CENTER.ACTIONS.FOCUS_REPLY.LABEL'),
    description: t(
      'ACCESSIBILITY.ACTION_CENTER.ACTIONS.FOCUS_REPLY.DESCRIPTION'
    ),
    shortcut: 'Alt Shift R',
    keywords: t('ACCESSIBILITY.ACTION_CENTER.ACTIONS.FOCUS_REPLY.KEYWORDS'),
    run: () => requestExistingAction('focus-reply'),
  },
  ...[
    ['home', 'CONVERSATIONS'],
    ['contacts_dashboard_index', 'CONTACTS'],
    ['campaigns_whatsapp_index', 'CAMPAIGNS'],
    ['sms_configuration', 'SMS'],
    ['tickets_dashboard_index', 'TICKETS'],
    ['locations_index', 'LOCATIONS'],
    ['helpcenter_viewer', 'HELP_CENTER'],
    ['account_overview_reports', 'REPORTS'],
    ['billing_settings_index', 'APP_STORE'],
    ['lumerai_settings_index', 'LUMERAI'],
    ['lumerai_flows', 'WORKFLOWS'],
    ['agent_list', 'USER_MANAGEMENT'],
    ['general_settings_index', 'SETTINGS'],
  ].map(([name, routeKey]) => {
    // routeKey is selected only from the explicit route table above.
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    const label = t(`ACCESSIBILITY.ACTION_CENTER.ROUTES.${routeKey}`);
    return {
      id: `navigate-${name}`,
      label: t('ACCESSIBILITY.ACTION_CENTER.OPEN_ROUTE', { label }),
      description: t('ACCESSIBILITY.ACTION_CENTER.NAVIGATE_TO_ROUTE', {
        label,
      }),
      // routeKey is selected only from the explicit route table above.
      // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      keywords: t(`ACCESSIBILITY.ACTION_CENTER.ROUTE_KEYWORDS.${routeKey}`),
      run: () => navigateTo(name, label),
    };
  }),
]);

const filteredActions = computed(() => {
  const normalizedQuery = query.value.trim().toLocaleLowerCase();
  if (!normalizedQuery) return actions.value;
  return actions.value.filter(action =>
    `${action.label} ${action.description} ${action.keywords || ''}`
      .toLocaleLowerCase()
      .includes(normalizedQuery)
  );
});

const closeActionCenter = () => {
  isOpen.value = false;
  query.value = '';
  activeIndex.value = 0;
  nextTick(() => lastFocusedElement.value?.focus?.());
};

const openActionCenter = async () => {
  lastFocusedElement.value = document.activeElement;
  isOpen.value = true;
  await nextTick();
  searchInput.value?.focus();
  announce(t('ACCESSIBILITY.ACTION_CENTER.OPENED_ANNOUNCEMENT'));
};

const executeAction = async action => {
  if (!action) return;
  closeActionCenter();
  await action.run();
};

const focusLandmark = direction => {
  const definitions = [
    [
      'aside, [role="complementary"]',
      t('ACCESSIBILITY.ACTION_CENTER.LANDMARKS.MAIN_MENU'),
    ],
    [
      '#conversation-list-region',
      t('ACCESSIBILITY.ACTION_CENTER.LANDMARKS.CONVERSATION_LIST'),
    ],
    [
      '[aria-label="Conversa atual"]',
      t('ACCESSIBILITY.ACTION_CENTER.LANDMARKS.CURRENT_CONVERSATION'),
    ],
    ['#reply-composer', t('ACCESSIBILITY.ACTION_CENTER.LANDMARKS.REPLY_FIELD')],
    [
      '#main-content, main',
      t('ACCESSIBILITY.ACTION_CENTER.LANDMARKS.MAIN_CONTENT'),
    ],
  ];
  const landmarks = definitions
    .map(([selector, label]) => ({
      element: document.querySelector(selector),
      label,
    }))
    .filter(
      ({ element }, index, list) =>
        element && list.findIndex(item => item.element === element) === index
    );

  if (!landmarks.length) {
    announce(t('ACCESSIBILITY.ACTION_CENTER.NO_LANDMARKS'));
    return;
  }

  const containingIndex = landmarks.findIndex(({ element }) =>
    element.contains(document.activeElement)
  );
  if (containingIndex >= 0) landmarkIndex = containingIndex;
  landmarkIndex =
    (landmarkIndex + direction + landmarks.length) % landmarks.length;
  const landmark = landmarks[landmarkIndex];
  if (!landmark.element.hasAttribute('tabindex')) {
    landmark.element.setAttribute('tabindex', '-1');
    landmark.element.dataset.a11yTemporaryTabindex = 'true';
  }
  landmark.element.focus({ preventScroll: true });
  landmark.element.scrollIntoView?.({ block: 'nearest' });
  announce(
    t('ACCESSIBILITY.ANNOUNCER.REGION_POSITION', {
      region: landmark.label,
      current: landmarkIndex + 1,
      total: landmarks.length,
    })
  );
};

const handleDialogKeydown = event => {
  if (event.key === 'Escape') {
    event.preventDefault();
    closeActionCenter();
    return;
  }
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    if (!filteredActions.value.length) return;
    event.preventDefault();
    const offset = event.key === 'ArrowDown' ? 1 : -1;
    activeIndex.value =
      (activeIndex.value + offset + filteredActions.value.length) %
      filteredActions.value.length;
    return;
  }
  if (event.key === 'Enter' && document.activeElement === searchInput.value) {
    event.preventDefault();
    executeAction(filteredActions.value[activeIndex.value]);
    return;
  }
  if (event.key !== 'Tab') return;

  const focusable = [...dialog.value.querySelectorAll('button, input')].filter(
    element => !element.disabled
  );
  const first = focusable[0];
  const last = focusable.at(-1);
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

const handleGlobalKeydown = event => {
  if (event.key === 'F2') {
    event.preventDefault();
    if (isOpen.value) {
      closeActionCenter();
    } else {
      openActionCenter();
    }
    return;
  }
  if (isOpen.value || event.defaultPrevented) return;
  if (!preferences.value.keyboardNavigation) return;

  if (event.key === 'F6') {
    event.preventDefault();
    focusLandmark(event.shiftKey ? -1 : 1);
    return;
  }

  if (!event.altKey || !event.shiftKey || isEditableElement(event.target)) {
    return;
  }

  const action = {
    KeyG: claimNextConversation,
    KeyU: assignCurrentConversation,
    KeyQ: returnCurrentConversationToQueue,
  }[event.code];
  if (!action) return;
  event.preventDefault();
  action();
};

watch(filteredActions, () => {
  activeIndex.value = 0;
});

watch(
  () => route.fullPath,
  () => {
    if (isOpen.value) closeActionCenter();
    landmarkIndex = -1;
  }
);

onMounted(() => window.addEventListener('keydown', handleGlobalKeydown));
onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleGlobalKeydown);
  document
    .querySelectorAll('[data-a11y-temporary-tabindex="true"]')
    .forEach(element => element.removeAttribute('tabindex'));
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[100001] flex items-start justify-center p-4 pt-[10vh] bg-black/70"
      @click.self="closeActionCenter"
    >
      <section
        ref="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="a11y-action-center-title"
        aria-describedby="a11y-action-center-description"
        class="w-full max-w-2xl overflow-hidden border rounded-xl shadow-2xl border-n-strong bg-n-solid-1 text-n-slate-12"
        @keydown="handleDialogKeydown"
      >
        <header
          class="flex items-start justify-between gap-4 p-5 border-b border-n-weak"
        >
          <div>
            <h1 id="a11y-action-center-title" class="text-xl font-semibold">
              {{ copy.title }}
            </h1>
            <p
              id="a11y-action-center-description"
              class="mt-1 text-sm text-n-slate-11"
            >
              {{ copy.description }}
            </p>
          </div>
          <button
            type="button"
            class="px-3 py-2 rounded-lg hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
            :aria-label="copy.closeLabel"
            @click="closeActionCenter"
          >
            {{ copy.close }}
          </button>
        </header>

        <div class="p-4">
          <label for="a11y-action-search" class="sr-only">{{
            copy.searchLabel
          }}</label>
          <input
            id="a11y-action-search"
            ref="searchInput"
            v-model="query"
            type="search"
            autocomplete="off"
            class="w-full px-4 py-3 border rounded-lg border-n-strong bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
            :placeholder="copy.searchPlaceholder"
            aria-controls="a11y-action-list"
            :aria-activedescendant="
              filteredActions[activeIndex]
                ? `a11y-action-${filteredActions[activeIndex].id}`
                : undefined
            "
          />
        </div>

        <p v-if="!filteredActions.length" role="status" class="px-5 pb-5">
          {{ copy.empty }}
        </p>
        <ul
          v-else
          id="a11y-action-list"
          role="listbox"
          :aria-label="copy.actionsLabel"
          class="max-h-[55vh] px-2 pb-3 overflow-y-auto"
        >
          <li
            v-for="(action, index) in filteredActions"
            :key="action.id"
            role="presentation"
          >
            <button
              :id="`a11y-action-${action.id}`"
              type="button"
              role="option"
              :aria-selected="index === activeIndex"
              class="flex items-center justify-between w-full gap-4 px-4 py-3 text-left rounded-lg"
              :class="
                index === activeIndex
                  ? 'bg-n-brand text-white'
                  : 'hover:bg-n-alpha-2'
              "
              @focus="activeIndex = index"
              @mouseenter="activeIndex = index"
              @click="executeAction(action)"
            >
              <span>
                <span class="block font-medium">{{ action.label }}</span>
                <span class="block mt-1 text-sm opacity-80">{{
                  action.description
                }}</span>
              </span>
              <kbd
                v-if="action.shortcut"
                class="shrink-0 px-2 py-1 text-xs border rounded border-current"
              >
                {{ action.shortcut }}
              </kbd>
            </button>
          </li>
        </ul>
      </section>
    </div>
  </Teleport>
</template>
